import { Alert, Box, Button, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  useStorageAction, useStorageView, type ConfigBlock, type ConfigPatch, type StorageAdmin, type StorageView,
} from '@/api/storage-admin';
import { FormFields, useFieldForm } from '@/components/fields';
import { changedValues, serverError, StorageSummary, type FieldGroup } from './common';
import { platformGroups, TENANT_GROUPS } from './configFields';
import { IntegrityCard } from './IntegrityCard';
import { LogsSection } from './LogsSection';
import { PoliciesSection } from './PoliciesSection';
import { ProfilesSection } from './ProfilesSection';
import { RecycleBinSection } from './RecycleBinSection';
import { UsageSection } from './UsageSection';

type Section = 'settings' | 'servers' | 'policies' | 'usage' | 'bin' | 'logs';

/**
 * Everything about one workspace's file storage. Settings → Storage shows it
 * to the workspace ADMIN and the Platform Console to the operator; which
 * sections appear follows what the admin object offers.
 */
export function StorageAdminPanel({ admin, corsOrigins }: { admin: StorageAdmin; corsOrigins?: string[] }) {
  const view = useStorageView(admin);
  const [section, setSection] = useState<Section>('settings');
  const sections: [Section, string][] = [
    ['settings', 'Settings'],
    ['servers', 'S3 servers'],
    ...(admin.listPolicies ? [['policies', 'Policies'] as [Section, string]] : []),
    ...(admin.usage ? [['usage', 'Usage'] as [Section, string]] : []),
    ...(admin.recycleBin ? [['bin', 'Recycle bin'] as [Section, string]] : []),
    ...(admin.events ? [['logs', 'Logs'] as [Section, string]] : []),
  ];

  if (view.isLoading) return <CircularProgress size={20} />;
  if (view.error || !view.data) return <Alert severity="error">{serverError(view.error).message}</Alert>;
  return (
    <Stack spacing={2}>
      <StorageSummary view={view.data} />
      {(view.data.integrity || admin.runIntegrity) && <IntegrityCard admin={admin} summary={view.data.integrity} />}
      <Tabs value={section} onChange={(_e, v) => setSection(v as Section)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {sections.map(([value, label]) => <Tab key={value} value={value} label={label} />)}
      </Tabs>
      {section === 'settings' && <ConfigSection admin={admin} view={view.data} />}
      {section === 'servers' && <ProfilesSection admin={admin} view={view.data} corsOrigins={corsOrigins} />}
      {section === 'policies' && <PoliciesSection admin={admin} />}
      {section === 'usage' && <UsageSection admin={admin} />}
      {section === 'bin' && <RecycleBinSection admin={admin} />}
      {section === 'logs' && <LogsSection admin={admin} />}
    </Stack>
  );
}

function ConfigSection({ admin, view }: { admin: StorageAdmin; view: StorageView }) {
  const save = useStorageAction(admin, (patch: ConfigPatch) => admin.save(patch));
  const platform = useMemo(() => platformGroups(view.profiles), [view.profiles]);
  return (
    <Stack spacing={3}>
      {view.platform_editable && (
        <ConfigForm title="Platform" groups={platform} block={view.platform}
          hint="Where files are stored and the limits this workspace works within."
          onSave={(patch) => save.mutateAsync({ platform: patch }).then((v) => v.platform)} />
      )}
      <ConfigForm title="This workspace" groups={TENANT_GROUPS} block={view.tenant}
        hint="Quotas, what each part of the app accepts, and how space is saved."
        onSave={(patch) => save.mutateAsync({ tenant: patch }).then((v) => v.tenant)} />
    </Stack>
  );
}

function ConfigForm({ title, hint, groups, block, onSave }: {
  title: string;
  hint: string;
  groups: FieldGroup[];
  block: ConfigBlock;
  /** Sends only what changed; resolves with the block as the server now holds it. */
  onSave: (patch: ConfigBlock) => Promise<ConfigBlock>;
}) {
  const fields = useMemo(() => groups.flatMap((g) => g.fields), [groups]);
  const form = useFieldForm(fields, block);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const { reset, isDirty } = form;

  // Pick up the server's values when they change, but never over unsaved edits.
  useEffect(() => {
    if (!isDirty) reset(block);
  }, [block, isDirty, reset]);

  const submit = async () => {
    setError(undefined);
    setSaved(false);
    if (!form.validate()) return;
    setBusy(true);
    try {
      reset(await onSave(changedValues(fields, block, form.values)));
      setSaved(true);
    } catch (e) {
      const { field, message } = serverError(e);
      if (field && fields.some((f) => f.name === field)) form.setErrors({ [field]: message });
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>
      <Stack spacing={3}>
        {groups.map((g) => (
          <Box key={g.title}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>{g.title}</Typography>
            {g.hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{g.hint}</Typography>}
            <FormFields form={form} fields={g.fields} />
          </Box>
        ))}
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={submit} disabled={busy || !isDirty}>{busy ? 'Saving…' : 'Save'}</Button>
        <Button onClick={() => reset(block)} disabled={busy || !isDirty}>Discard changes</Button>
        {saved && !isDirty && <Typography variant="body2" color="success.main">Saved.</Typography>}
      </Stack>
    </Paper>
  );
}
